import type { RefObject } from "react";
import { useTranslation } from "react-i18next";
import { FORM_COMPACT_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import { classNames } from "../../../ui/styles/classNames";
import ConflictLevelBadge from "../page/ConflictLevelBadge";
import type { OutlineFilter, OutlineGroup, OutlineRow } from "./reviewOutline";
import Chip from "../../../ui/feedback/Chip";
import SegmentedControl from "../../../ui/navigation/SegmentedControl";

const FILTERS: readonly OutlineFilter[] = ["all", "needs_me", "open", "duplicates"];

/**
 * The outline (curation-ui.md CU-2): one row per review unit, grouped, worst first, with
 * the filters All, Needs me, Open and Possible duplicates and a text filter (`/`). An ARIA
 * tree; the row in focus is the unit the focus pane shows.
 */
export default function OutlinePane({
  groups,
  current,
  filter,
  text,
  filterInput,
  onFilter,
  onText,
  onSelect,
  approverNames,
  presentAt,
}: {
  groups: readonly OutlineGroup[];
  current: string;
  filter: OutlineFilter;
  text: string;
  filterInput: RefObject<HTMLInputElement | null>;
  onFilter: (filter: OutlineFilter) => void;
  onText: (text: string) => void;
  onSelect: (key: string) => void;
  approverNames: (row: OutlineRow) => readonly string[];
  /** The others who look at the unit now (CU-10 presence). */
  presentAt: (key: string) => readonly string[];
}) {
  const { t } = useTranslation();
  return (
    <nav className="space-y-2" aria-label={t("reviews.curation.outline.label")}>
      <SegmentedControl
        label={t("reviews.curation.outline.filters")}
        options={FILTERS.map((option) => ({ key: option, label: t(`reviews.curation.outline.filter.${option}`) }))}
        selected={filter}
        onSelect={onFilter}
      />
      <input
        ref={filterInput}
        className={FORM_COMPACT_CONTROL_CLASS}
        type="search"
        aria-label={t("reviews.curation.outline.search")}
        placeholder={t("reviews.curation.outline.search")}
        aria-keyshortcuts="/"
        value={text}
        onChange={(event) => {
          onText(event.target.value);
        }}
      />
      <ul role="tree" aria-label={t("reviews.curation.outline.units")} className="space-y-2">
        {groups.map((group) => (
          <li key={group.key} role="treeitem" aria-expanded={true} aria-selected={false}>
            <p className="app-text-muted px-1 text-xs font-semibold uppercase tracking-wide">
              {t(`reviews.curation.outline.groups.${group.key}`)}
              {group.key !== "conversation" ? <span className="ml-1">{group.rows.length}</span> : null}
            </p>
            <ul role="group" className="mt-0.5 space-y-0.5">
              {group.rows.map((row) => (
                <li key={row.key} role="treeitem" aria-selected={row.key === current}>
                  <button
                    type="button"
                    className={classNames("app-nav-item flex w-full flex-wrap items-center gap-1 rounded px-2 py-1 text-left text-sm", row.key === current ? "app-nav-item-active" : "")}
                    aria-current={row.key === current ? "true" : undefined}
                    onClick={() => {
                      onSelect(row.key);
                    }}
                  >
                    {/* The whole name, wrapped, even in a narrow margin (usability round 4: "D…", "Dispensat…" said nothing). */}
                    <span className="min-w-[8rem] flex-1 font-medium [overflow-wrap:anywhere]" title={row.unit.term ?? row.name}>
                      {row.unit.kind === "conversation" ? t("reviews.curation.outline.conversation") : row.name}
                    </span>
                    {row.unit.change !== null ? <span className="app-text-muted text-xs">{t(`reviews.curation.changes.${row.unit.change}`)}</span> : null}
                    {row.level !== null && row.level !== "info" ? <ConflictLevelBadge level={row.level} /> : null}
                    {row.openCandidates > 0 ? <Chip tone="warning">{t("reviews.curation.outline.duplicate")}</Chip> : null}
                    {row.myMark !== null ? (
                      <span className={classNames("text-xs font-semibold", row.myMark === "ok" ? "app-status-text-success" : "app-status-text-warning")}>
                        {t(`reviews.curation.marks.short.${row.myMark}`)}
                      </span>
                    ) : null}
                    {presentAt(row.key).length > 0 ? (
                      <Chip tone="info" title={t("reviews.collaboration.presence.isHere", { names: presentAt(row.key).join(", ") })}>
                        {t("reviews.collaboration.presence.isHere", { names: presentAt(row.key).join(", ") })}
                      </Chip>
                    ) : null}
                    {row.needsMe ? <span className="app-status-text-warning text-xs" title={t("reviews.curation.outline.needsMe")} aria-label={t("reviews.curation.outline.needsMe")}>●</span> : null}
                    {approverNames(row).length > 0 ? <span className="app-status-text-success basis-full text-xs">✓ {approverNames(row).join(", ")}</span> : null}
                  </button>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </nav>
  );
}
